import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Stack, Button, CircularProgress,
  Alert, Chip, Link,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HubIcon from '@mui/icons-material/Hub';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getNetworkStatus, discoverNetwork, loadNetwork, getMembers, getAppConfig } from '../../services/api';
import TrustGraphLoader from '../TrustGraphLoader';

const GREEN = '#76B900';

// tab indices used by onNavigate
const TAB = { MANAGE_CREDENTIALS: 2, SIGNING: 3, ISSUER: 4, MEMBERS: 6 };

function MetricCard({ icon, color, value, label, sub }) {
  return (
    <Card elevation={0} sx={{ height: '100%', border: '1px solid #3D3D3D', background: `linear-gradient(135deg, ${color}26 0%, transparent 70%)` }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: color, width: 52, height: 52 }}>{icon}</Avatar>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
            <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
        {sub && <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function Step({ done, num, title, desc, actionLabel, onAction }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
      {done ? <CheckCircleIcon sx={{ color: GREEN }} /> : <RadioButtonUncheckedIcon color="disabled" />}
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{num}. {title}</Typography>
        <Typography variant="body2" color="text.secondary">{desc}</Typography>
      </Box>
      {onAction && (
        <Button size="small" variant={done ? 'text' : 'outlined'} onClick={onAction} endIcon={<ArrowForwardIcon />}>
          {actionLabel}
        </Button>
      )}
    </Stack>
  );
}

const Dashboard = ({ onNavigate }) => {
  const [status, setStatus] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingNetwork, setLoadingNetwork] = useState(false);
  const [error, setError] = useState(null);
  const [loaderMembers, setLoaderMembers] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getNetworkStatus();
      setStatus(s);
      if (!s.membersLoaded) {
        try { setDiscovery(await discoverNetwork()); } catch (e) { /* noop */ }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { getAppConfig().then(setConfig).catch(() => {}); }, []);

  const handleLoad = async () => {
    try {
      setLoadingNetwork(true); setError(null);
      await loadNetwork();
      const roster = await getMembers();
      setLoaderMembers(roster.members || []);   // triggers the animated Trust Graph loader
    } catch (e) {
      setError(e.response?.status === 401
        ? 'Please sign in (top-right) to load your member publishers.'
        : 'Failed to load members from the Noosphere Trust Graph.');
    } finally {
      setLoadingNetwork(false);
    }
  };

  const handleLoaderDone = async () => {
    setLoaderMembers(null);
    await refresh();
    if (onNavigate) onNavigate(6); // jump to the Members roster
  };

  if (loading || !status) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {loaderMembers && (
        <TrustGraphLoader members={loaderMembers} orgName={config?.orgName} onDone={handleLoaderDone} />
      )}
      <Typography variant="h4" sx={{ fontWeight: 700 }}>{config?.orgName || 'Credential Console'}</Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Issue verifiable credentials to your member publishers — portable, tamper-proof proofs of the
        claims you stand behind, verifiable by anyone, anywhere.
      </Typography>

      {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Discovery hero — shown until the network is loaded */}
      {!status.membersLoaded && (
        <Card elevation={0} sx={{ mb: 3, border: `1px solid ${GREEN}`, background: `linear-gradient(135deg, ${GREEN}1f 0%, transparent 70%)` }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HubIcon sx={{ color: GREEN }} /> Trust Graph discovery
                </Typography>
                {discovery ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    We found your organization on the Trust Graph. Load them here so you can issue and
                    manage their credentials.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Reading the Noosphere Trust Graph…</Typography>
                )}
              </Box>
              <Button variant="contained" size="large" onClick={handleLoad} disabled={loadingNetwork || !discovery}
                startIcon={loadingNetwork ? <CircularProgress size={18} /> : <GroupsIcon />}>
                Load member publishers
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard icon={<GroupsIcon />} color="#2E7DF6" value={status.memberCount} label="Member Publishers" sub="From the Noosphere Trust Graph" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard icon={<DescriptionIcon />} color={GREEN} value={status.credentialTypeCount} label="Credential Types" sub="Verifiable credentials you offer" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard icon={<WorkspacePremiumIcon />} color="#9C27B0" value={status.issuedCount} label="Credentials Issued" sub="To your member publishers" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard icon={<VpnKeyIcon />} color="#00B8A9" value={status.signingKeyConfigured ? 'Ready' : '—'} label="Signing Key" sub="Signs the credentials you issue" />
        </Grid>
      </Grid>

      {/* Guided steps */}
      <Card elevation={0} sx={{ mt: 3, border: '1px solid #3D3D3D' }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Set up your credential authority</Typography>
          <Step done={status.membersLoaded} num={1} title="Load your member publishers"
            desc="Discover your network from the Noosphere Trust Graph and load your member publishers."
            actionLabel={status.membersLoaded ? 'View members' : 'Load'} onAction={status.membersLoaded ? () => onNavigate?.(TAB.MEMBERS) : handleLoad} />
          <Step done={status.credentialTypeCount > 0} num={2} title="Create verifiable credentials"
            desc="Define the credentials you offer your members — the claims and metadata each one carries."
            actionLabel="Manage credentials" onAction={() => onNavigate?.(TAB.MANAGE_CREDENTIALS)} />
          <Step done={status.signingKeyConfigured} num={3} title="Configure your signing key"
            desc="Choose the key that cryptographically signs the credentials you issue."
            actionLabel="Signing keys" onAction={() => onNavigate?.(TAB.SIGNING)} />
          <Step done={status.issuedCount > 0} num={4} title="Issue credentials to members"
            desc="Issue verifiable credentials to your member publishers."
            actionLabel="Members" onAction={() => onNavigate?.(TAB.MEMBERS)} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
